'use client'

import { ChevronRight, type LucideIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem
} from '@/components/ui/sidebar'
import { ICON_SIZE_1 } from '@/utils/constant'

type NavItem = {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: NavItem[]
}

type NavGroup = {
    items: NavItem[]
    label?: string
}

export function NavMain({ items }: { items: (NavItem[] | NavGroup)[] }) {
    const navigate = useNavigate()

    return (
        <>
            {items.map((group, groupIndex) => {
                const isGroupWithLabel = 'label' in group && group.label !== undefined
                const groupItems: NavItem[] = isGroupWithLabel ? (group as NavGroup).items : (group as NavItem[])
                const groupLabel = isGroupWithLabel ? (group as NavGroup).label : undefined

                return (
                    <SidebarGroup key={`group-${groupIndex}`}>
                        {groupLabel && (
                            <SidebarGroupLabel className="group/label text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                                {groupLabel}
                            </SidebarGroupLabel>
                        )}
                        <SidebarMenu>
                            {groupItems.map((item) => (
                                <Collapsible key={item.title} asChild defaultOpen={item.isActive} className="group/collapsible">
                                    <SidebarMenuItem>
                                        <CollapsibleTrigger asChild>
                                            <SidebarMenuButton
                                                tooltip={item.title}
                                                isActive={item.isActive}
                                                onClick={() => {
                                                    if (!item.items) navigate(item.url)
                                                }}
                                                className="cursor-pointer"
                                            >
                                                {item.icon && <item.icon size={ICON_SIZE_1} onClick={() => navigate(item.url)} />}
                                                <span onClick={() => navigate(item.url)}>{item.title}</span>
                                                {item.items && <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />}
                                            </SidebarMenuButton>
                                        </CollapsibleTrigger>
                                        {item.items && (
                                            <CollapsibleContent>
                                                <SidebarMenuSub>
                                                    {item.items.map((subItem) => (
                                                        <SidebarMenuSubItem key={subItem.title}>
                                                            <SidebarMenuSubButton asChild isActive={subItem.isActive}>
                                                                <span onClick={() => navigate(subItem.url)} className="flex items-center gap-2 cursor-pointer">
                                                                    {subItem.icon && <subItem.icon size={ICON_SIZE_1} />}
                                                                    {subItem.title}
                                                                </span>
                                                            </SidebarMenuSubButton>
                                                        </SidebarMenuSubItem>
                                                    ))}
                                                </SidebarMenuSub>
                                            </CollapsibleContent>
                                        )}
                                    </SidebarMenuItem>
                                </Collapsible>
                            ))}
                        </SidebarMenu>
                    </SidebarGroup>
                )
            })}
        </>
    )
}
